import React, {useEffect, useRef, useState} from 'react';
import {Alert} from 'react-native';

import {Container, SectionHeader, Title} from './styles';
import {
  ChangePaymentMethodItem,
  PaymentMethodItemFooterBanners,
} from '../../../ChangePaymentMethodItem';
import {UserCreditCardList} from '../../../UserCreditCardList';
import {PedidoDTO} from '../../../../../../dtos/PedidoDTO';
import {Localidade} from '../..';
import {ParcelasDTO} from '../../../../../../dtos/ParcelasDTO';
import {InstallmentsModal} from '../../../InstallmentsModal';
import {UserCreditCardsDTO} from '../../../../../../dtos/UserCreditCardsDTO';
import use3DS from '../../../../../../utils/payments/3ds';
import { useAuth } from '../../../../../../context/auth';

type PaymentMethod = 'PIX' | 'TICKETLOG' | UserCreditCardsDTO | null;

type Props = {
  tipo_operacao?: string;
  pedidos: PedidoDTO;
  localidade: Localidade;
  availableInstallments: ParcelasDTO[];
  selectedInstallment: ParcelasDTO | null;
  setSelectedInstallment: (installment: ParcelasDTO) => void;
  isLoadingInstallments: boolean;
  on3DSProviderChange: (provider: string | null) => void;
  onLoadingChange: (loading: boolean) => void;
  onScrollToInstallments: (y: number) => void;
  onLayout?: (event: any) => void;
  selectedPaymentMethod: PaymentMethod;
  changePaymentMethod: (method: PaymentMethod) => void;
  enableVale?: boolean;
  enableBrbDiscount?: boolean;
  banners?: PaymentMethodItemFooterBanners;
  autoSelectFirst?: boolean;
};

export function CheckoutCreditCard({
  tipo_operacao,
  pedidos,
  localidade,
  availableInstallments,
  selectedInstallment,
  setSelectedInstallment,
  isLoadingInstallments,
  on3DSProviderChange,
  onLoadingChange,
  onScrollToInstallments,
  onLayout,
  selectedPaymentMethod,
  changePaymentMethod,
  enableVale,
  enableBrbDiscount,
  banners,
  autoSelectFirst,
}: Props) {
  const {checkCardIs3ds} = use3DS();
  const {clientId} = useAuth();

  const isSelectedCard = typeof selectedPaymentMethod !== 'string';
  const selectedCard = isSelectedCard
    ? (selectedPaymentMethod as UserCreditCardsDTO)
    : undefined;

  const [threeDSProvider, setThreeDSProvider] = useState<string | null>(null);
  const [isLoadingCard, setIsLoadingCard] = useState(false);
  const [showInstallmentsModal, setShowInstallmentsModal] = useState(false);
  const [installmentPulseKey, setInstallmentPulseKey] = useState(0);
  const checkCard3dsRequestIdRef = useRef(0);
  const shouldScrollToInstallmentRef = useRef(false);

  useEffect(() => {
    on3DSProviderChange(threeDSProvider);
  }, [threeDSProvider]);

  useEffect(() => {
    onLoadingChange(isLoadingCard);
  }, [isLoadingCard]);

  useEffect(() => {
    if (threeDSProvider === 'stripe') {
      setShowInstallmentsModal(false);
    }
  }, [threeDSProvider]);

  useEffect(() => {
    if (selectedCard?.id) {
      handleCardChange(selectedCard);
    }
  }, [selectedCard?.id]);

  const handleCardChange = async (card: UserCreditCardsDTO) => {
    const requestId = ++checkCard3dsRequestIdRef.current;
    setThreeDSProvider(null);
    setIsLoadingCard(true);
    try {
      const {data} = await checkCardIs3ds(card.id, tipo_operacao, clientId);
      if (requestId !== checkCard3dsRequestIdRef.current) {
        return;
      }
      setThreeDSProvider(data['3ds']);
    } catch (error) {
      Alert.alert(
        'Ocorreu um problema',
        'Não foi possível verificar o cartão, tente novamente mais tarde.',
      );
    } finally {
      if (requestId === checkCard3dsRequestIdRef.current) {
        setIsLoadingCard(false);
      }
    }
  };

  const handleSelectCard = (_card: UserCreditCardsDTO) => {
    shouldScrollToInstallmentRef.current = true;
  };

  const handleInstallmentLayout = (y: number) => {
    if (!shouldScrollToInstallmentRef.current) {
      return;
    }
    shouldScrollToInstallmentRef.current = false;
    requestAnimationFrame(() => {
      onScrollToInstallments(y);
      setTimeout(() => {
        setInstallmentPulseKey(prev => prev + 1);
      }, 150);
    });
  };

  function handleSelectInstallment(installment: ParcelasDTO) {
    setSelectedInstallment(installment);
    setShowInstallmentsModal(false);
  }

  return (
    <>
      <Container onLayout={onLayout}>

        <SectionHeader>
          <Title>Cartão</Title>
        </SectionHeader>

        <UserCreditCardList
          pedidos={pedidos ? pedidos : null}
          localidade={localidade ? localidade.region : null}
          selectedInstallment={selectedInstallment}
          canInstallments={threeDSProvider !== 'stripe'}
          isLoadingInstallments={isLoadingInstallments}
          onPressInstallments={() => setShowInstallmentsModal(true)}
          onSelectCard={handleSelectCard}
          onInstallmentLayout={handleInstallmentLayout}
          pulseInstallmentKey={installmentPulseKey}
          isStripe={threeDSProvider === 'stripe'}
          enableVale={enableVale}
          enableBrbDiscount={enableBrbDiscount}
          selectedPaymentMethod={selectedPaymentMethod}
          changePaymentMethod={changePaymentMethod}
          autoSelectFirst={autoSelectFirst}
        />
      </Container>

      <ChangePaymentMethodItem enableVale={enableVale} banners={banners} />

      <InstallmentsModal
        showModal={showInstallmentsModal && threeDSProvider !== 'stripe'}
        onDismiss={() => setShowInstallmentsModal(false)}
        isLoading={isLoadingInstallments}
        installments={availableInstallments}
        voucher={selectedCard?.voucher}
        onSelect={handleSelectInstallment}
        selectedInstallmentCount={selectedInstallment?.installmentCount}
      />
    </>
  );
}
